import {
  GatewayAssujetti,
  GatewayConformite,
  GatewayQuestionnaire,
} from "@conformite/gateway";
import { Workbook } from "exceljs";
import { parseAnswerValue } from "@src/components/questionnaire/answerValue";
import { computeOptionColorOverrides } from "@src/components/questionnaire/conditionalColors";
import { computeVisibleQuestionIds } from "@src/components/questionnaire/QuestionnaireForm/useQuestionConditionEvaluator";
import {
  AssujettiIdentity,
  AssujettiQuestionnaireExportData,
} from "@src/data/assujetti/assujettiQuestionnaireExport";
import { downloadFile } from "@src/helper/file.helper";
import {
  getRemediationStatus,
  REMEDIATION_QUESTION_LABEL,
} from "@src/helper/remediation.helper";

type Axe = GatewayConformite.AxeConformite;
type QuestionnaireDefinition =
  GatewayQuestionnaire.QuestionnaireDefinitionResponse;
type QuestionDefinition = GatewayQuestionnaire.QuestionDefinitionResponse;
type InstanceResponse = GatewayQuestionnaire.QuestionnaireInstanceResponse;
type QuestionnaireInstance = InstanceResponse | null;
type OptionColor = GatewayQuestionnaire.OptionColor;

const COMMENT_QUESTION_LABEL = "Commentaire";
const NON_CONCERNE = "Non concerné";

const CELL_COLORS: Record<OptionColor, { fill: string; font: string }> = {
  green: { fill: "FFDFF7EC", font: "FF037C5E" },
  red: { fill: "FFFFF0F0", font: "FFD43535" },
  orange: { fill: "FFFDF1DB", font: "FFA88200" },
  blue: { fill: "FFECF5FF", font: "FF0067E6" },
};

const COLOR_SEVERITY: OptionColor[] = ["red", "orange", "green", "blue"];

export const RoleAssujettiLabels: Record<
  GatewayAssujetti.RoleAssujetti,
  string
> = {
  REPRESENTANT_LEGAL: "Représentant légal de la structure",
  ASSOCIE: "Associé",
  DIRECTEUR_GENERAL: "Directeur général",
  DIRECTEUR_ADMINISTRATIF: "Directeur/responsable Administratif",
  DIRECTEUR_CONFORMITE: "Directeur conformité/juridique",
  DIRECTEUR_MARKETING: "Directeur Marketing / Communication",
  DIRECTEUR_COMMERCIAL: "Directeur/Responsable commercial",
  CHARGE_CLIENTELE: "Chargé de clientèle",
  JURISTE: "Souscripteur/juriste",
  GESTIONNAIRE: "Gestionnaire",
  CHEF_DE_PROJET: "Chef de projet",
  RESPONSABLE_BUREAU: "Responsable de bureau",
  COMMERCE: "Commerce",
  GESTION: "Gestion",
  BACK_OFFICE: "Back office",
};

const AXIS_SHEET_NAMES: Partial<Record<Axe, string>> = {
  [GatewayConformite.AxeConformite.HONORABILITE]: "Honorabilité",
  [GatewayConformite.AxeConformite.CAPACITE_PRO_IAS]: "Capacité pro IAS",
  [GatewayConformite.AxeConformite.CAPACITE_PRO_IOBSP]: "Capacité pro IOBSP",
  [GatewayConformite.AxeConformite.FORMATION_CONTINUE_IAS]:
    "Formation continue IAS",
  [GatewayConformite.AxeConformite.FORMATION_CONTINUE_IOBSP]:
    "Formation continue IOBSP",
};

const IDENTITY_HEADERS = ["Nom", "Prénom", "Email", "Rôle"];
const identityCells = (identity: AssujettiIdentity): string[] => [
  identity.name,
  identity.surname,
  identity.email ?? "",
  RoleAssujettiLabels[identity.role] ?? "",
];

const orderedQuestions = (
  definition: QuestionnaireDefinition
): QuestionDefinition[] =>
  [...definition.questions].sort((a, b) => a.order - b.order);

const findAnswer = (instance: InstanceResponse, question: QuestionDefinition) =>
  instance.answers.find((a) => a.label === question.label) ??
  instance.answers.find((a) => a.questionDefinitionId === question.id);

export function commentToText(html: string | null): string {
  if (!html) return "";
  const withBreaks = html
    .replace(/<\/(p|div|li|h[1-6])>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n");
  const text =
    new DOMParser().parseFromString(withBreaks, "text/html").body.textContent ??
    "";
  return text
    .replace(/[ \t]+/g, " ")
    .replace(/\s*\n\s*/g, "\n")
    .replace(/\n{2,}/g, "\n")
    .trim();
}

/**
 * Réponse lisible d'une personne à une question : on apparie l'instance par
 * libellé (robuste aux versions, comme le formulaire), puis on traduit les IDs
 * d'options en libellés (multi-choix joints par ", "). La question
 * "Commentaire" est un cas à part : sa valeur est le champ `comment` de
 * l'instance, pas une réponse dans `answers`.
 */
export function resolveAnswerText(
  instance: QuestionnaireInstance,
  question: QuestionDefinition
): string {
  if (!instance) return "";
  if (question.label === COMMENT_QUESTION_LABEL)
    return commentToText(instance.comment);
  const answer = findAnswer(instance, question);
  if (!answer) return "";
  const optionIds = parseAnswerValue(answer.value);
  if (optionIds.length === 0) return "";
  const labelByOptionId = new Map(
    (answer.options ?? question.options).map((option) => [
      option.id,
      option.label,
    ])
  );
  return optionIds
    .map((optionId) => labelByOptionId.get(optionId) ?? optionId)
    .join(", ");
}

const answersByQuestionId = (
  instance: QuestionnaireInstance,
  questions: QuestionDefinition[]
): Record<string, string> => {
  const map: Record<string, string> = {};
  if (!instance) return map;
  questions.forEach((question) => {
    const answer = findAnswer(instance, question);
    if (answer) map[question.id] = answer.value;
  });
  return map;
};

const visibleQuestionSets = (
  axe: Axe,
  questions: QuestionDefinition[],
  persons: AssujettiQuestionnaireExportData["persons"]
): Set<string>[] =>
  persons.map((person) =>
    computeVisibleQuestionIds(
      questions,
      answersByQuestionId(person.instanceByAxe[axe] ?? null, questions)
    )
  );

const keepRelevantQuestions = (
  questions: QuestionDefinition[],
  visibleSets: Set<string>[]
): QuestionDefinition[] =>
  questions.filter(
    (question) =>
      !question.condition || visibleSets.some((set) => set.has(question.id))
  );

const cellText = (
  instance: QuestionnaireInstance,
  question: QuestionDefinition,
  isVisible: boolean
): string => (isVisible ? resolveAnswerText(instance, question) : NON_CONCERNE);

export function resolveCellColor(
  instance: QuestionnaireInstance,
  question: QuestionDefinition,
  definition: QuestionnaireDefinition,
  isVisible: boolean
): OptionColor | null {
  if (!isVisible || !instance) return null;
  if (question.label === COMMENT_QUESTION_LABEL) return null;
  const answer = findAnswer(instance, question);
  if (!answer) return null;
  const optionIds = parseAnswerValue(answer.value);
  if (optionIds.length === 0) return null;

  if (
    question.label === REMEDIATION_QUESTION_LABEL &&
    getRemediationStatus(instance) === "OUI"
  )
    return "red";

  const overrides = computeOptionColorOverrides(
    definition.slug,
    question,
    definition.questions,
    answersByQuestionId(instance, definition.questions)
  );
  const baseColor = new Map(
    question.options.map((option) => [option.id, option.color])
  );
  const colors = optionIds
    .map((id) => overrides[id] ?? baseColor.get(id))
    .filter((color): color is OptionColor => Boolean(color));

  return COLOR_SEVERITY.find((color) => colors.includes(color)) ?? null;
}

export type ExportSheet = {
  sheetName: string;
  headers: string[];
  rows: string[][];
  cellColors: (OptionColor | null)[][];
};

export function buildAxisSheet(
  axe: Axe,
  definition: QuestionnaireDefinition,
  persons: AssujettiQuestionnaireExportData["persons"]
): ExportSheet {
  const questions = orderedQuestions(definition);
  const visibleSets = visibleQuestionSets(axe, questions, persons);
  const keptQuestions = keepRelevantQuestions(questions, visibleSets);
  const rows: string[][] = [];
  const cellColors: (OptionColor | null)[][] = [];
  persons.forEach((person, index) => {
    const instance = person.instanceByAxe[axe] ?? null;
    const remediationNeeded = getRemediationStatus(instance) === "OUI";
    rows.push([
      ...identityCells(person.identity),
      ...keptQuestions.map((question) =>
        cellText(instance, question, visibleSets[index].has(question.id))
      ),
    ]);
    cellColors.push([
      ...IDENTITY_HEADERS.map((_, columnIndex) =>
        remediationNeeded && columnIndex < 2 ? "red" : null
      ),
      ...keptQuestions.map((question) =>
        resolveCellColor(
          instance,
          question,
          definition,
          visibleSets[index].has(question.id)
        )
      ),
    ]);
  });
  return {
    sheetName: AXIS_SHEET_NAMES[axe] ?? definition.name,
    headers: [...IDENTITY_HEADERS, ...keptQuestions.map((q) => q.label)],
    rows,
    cellColors,
  };
}

export type FlatCsvTable = {
  headers: string[];
  rows: string[][];
};

export function buildFlatCsvTable(
  data: AssujettiQuestionnaireExportData
): FlatCsvTable {
  const axes = data.axes.map(({ axe, definition }) => {
    const questions = orderedQuestions(definition);
    const visibleSets = visibleQuestionSets(axe, questions, data.persons);
    return {
      axe,
      definition,
      visibleSets,
      keptQuestions: keepRelevantQuestions(questions, visibleSets),
    };
  });
  const columns = axes.flatMap((axis) =>
    axis.keptQuestions.map((question) => ({
      axis,
      question,
      header: `${AXIS_SHEET_NAMES[axis.axe] ?? axis.definition.name} — ${question.label}`,
    }))
  );
  return {
    headers: [...IDENTITY_HEADERS, ...columns.map((column) => column.header)],
    rows: data.persons.map((person, index) => [
      ...identityCells(person.identity),
      ...columns.map(({ axis, question }) =>
        cellText(
          person.instanceByAxe[axis.axe] ?? null,
          question,
          axis.visibleSets[index].has(question.id)
        )
      ),
    ]),
  };
}

const exportFilename = (siren: string, millesime: number, extension: string) =>
  `personnes_assujetties_${siren}_${millesime}.${extension}`;

type ExportContext = {
  siren: string;
  millesime: number;
};

export async function exportAssujettisQuestionnaireToExcel(
  data: AssujettiQuestionnaireExportData,
  { siren, millesime }: ExportContext
): Promise<void> {
  const workbook = new Workbook();

  data.axes.forEach(({ axe, definition }) => {
    const sheet = buildAxisSheet(axe, definition, data.persons);
    const worksheet = workbook.addWorksheet(sheet.sheetName);
    const headerRow = worksheet.addRow(sheet.headers);
    headerRow.font = { bold: true };
    sheet.rows.forEach((row, rowIndex) => {
      const worksheetRow = worksheet.addRow(row);
      sheet.cellColors[rowIndex].forEach((color, columnIndex) => {
        if (!color) return;
        const cell = worksheetRow.getCell(columnIndex + 1);
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: CELL_COLORS[color].fill },
        };
        cell.font = { color: { argb: CELL_COLORS[color].font } };
      });
    });
    worksheet.columns.forEach((column) => {
      column.width = 28;
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  downloadFile(buffer, exportFilename(siren, millesime, "xlsx"));
}

const CSV_SEPARATOR = ";";
// BOM UTF-8 (U+FEFF) pour qu'Excel ouvre correctement les accents.
const UTF8_BOM = String.fromCharCode(0xfeff);

const escapeCsvField = (field: string): string =>
  /[";\r\n]/.test(field) ? `"${field.replace(/"/g, '""')}"` : field;

const toCsv = ({ headers, rows }: FlatCsvTable): string =>
  [headers, ...rows]
    .map((line) => line.map(escapeCsvField).join(CSV_SEPARATOR))
    .join("\r\n");

export function exportAssujettisQuestionnaireToCsv(
  data: AssujettiQuestionnaireExportData,
  { siren, millesime }: ExportContext
): void {
  const csv = toCsv(buildFlatCsvTable(data));
  const blob = new Blob([UTF8_BOM, csv], { type: "text/csv;charset=utf-8" });
  downloadFile(blob, exportFilename(siren, millesime, "csv"));
}
